import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

/** HDR mode + CDN·DVR + optional Dolby Vision / HLG panels.
 *  These are the "signal characteristics" the transmitter offers to affiliates. */
export default function OutputSignalsPanel({
  channel,
  hdrMode, setHdrMode,
  cdn, patchCdn,
  dv, setDv,
  hlg, setHlg,
  planLimits,
  togglePublish,
}) {
  return (
    <>
          {/* Video / HDR + CDN */}
          <div className="border-t border-zinc-800 pt-4 grid grid-cols-2 gap-4" data-testid="video-cdn-section">
            <div>
              <div className="font-display font-bold mb-2">Video · HDR</div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">HDR mode</Label>
              <select
                value={hdrMode}
                onChange={(e) => setHdrMode(e.target.value)}
                className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                data-testid="hdr-mode-select"
              >
                <option value="SDR">SDR (BT.709)</option>
                <option value="HDR10">HDR10 (PQ · BT.2020)</option>
                <option value="HLG">HLG (Hybrid Log-Gamma)</option>
                <option value="DolbyVision">Dolby Vision</option>
              </select>
              <div className="text-[10px] font-mono text-zinc-500 mt-2">
                Resolution: <span className="text-zinc-300">{channel.resolution}</span> · FPS: <span className="text-zinc-300">{channel.fps}</span>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="font-display font-bold">CDN · DVR</div>
                <button
                  onClick={togglePublish}
                  data-testid="cdn-publish-toggle"
                  className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border ${
                    cdn.publish_state === "live"
                      ? "border-emerald-500/60 text-emerald-400 bg-emerald-500/10"
                      : "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                  }`}
                >
                  {cdn.publish_state === "live" ? "● Publishing" : "Idle · click to publish"}
                </button>
              </div>
              <div className="space-y-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                    DVR window (sec)
                    {planLimits && (
                      <span className="text-zinc-400 ml-2">
                        max {Math.round(planLimits.limits.max_dvr_window_sec / 3600)}h ({planLimits.plan})
                      </span>
                    )}
                  </Label>
                  <Input
                    type="number" min="0" step="60"
                    max={planLimits?.limits?.max_dvr_window_sec}
                    value={cdn.dvr_window_sec}
                    onChange={(e) => patchCdn("dvr_window_sec", Number(e.target.value))}
                    className="mt-1 bg-black border-zinc-800 font-mono text-xs"
                    data-testid="cdn-dvr-window"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Segment (s)</Label>
                    <Input
                      type="number" min="1" step="1"
                      value={cdn.hls_segment_sec}
                      onChange={(e) => patchCdn("hls_segment_sec", Number(e.target.value))}
                      className="mt-1 bg-black border-zinc-800 font-mono text-xs"
                      data-testid="cdn-segment-sec"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Edge cache (s)</Label>
                    <Input
                      type="number" min="1" step="1"
                      value={cdn.edge_cache_sec}
                      onChange={(e) => patchCdn("edge_cache_sec", Number(e.target.value))}
                      className="mt-1 bg-black border-zinc-800 font-mono text-xs"
                    />
                  </div>
                </div>
                <div className="text-[10px] font-mono text-zinc-500">
                  DVR window: <span className="text-zinc-300">{Math.round(cdn.dvr_window_sec / 60)} min</span>
                </div>
              </div>
            </div>
          </div>

          {/* HDR extensions: Dolby Vision + HLG */}
          {(hdrMode === "DolbyVision" || hdrMode === "HDR10" || hdrMode === "HLG") && (
            <div className="border-t border-zinc-800 pt-4 grid grid-cols-2 gap-4" data-testid="hdr-extensions-section">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display font-bold">Dolby Vision · RPU</div>
                  <label className="flex items-center gap-1 text-[10px] text-zinc-400">
                    <input
                      type="checkbox"
                      checked={dv.enabled}
                      onChange={(e) => setDv({ ...dv, enabled: e.target.checked })}
                      className="accent-red-500"
                      data-testid="dv-enabled"
                    />
                    Enable
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Profile</Label>
                    <select
                      value={dv.profile}
                      onChange={(e) => setDv({ ...dv, profile: e.target.value })}
                      className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                      data-testid="dv-profile"
                    >
                      <option value="5">Profile 5 (single-layer)</option>
                      <option value="8.1">Profile 8.1 (BL+RPU, HDR10 compat)</option>
                      <option value="8.4">Profile 8.4 (BL+RPU, HLG compat)</option>
                      <option value="9">Profile 9 (BL+RPU, SDR compat)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">CM version</Label>
                    <select
                      value={dv.cm_version}
                      onChange={(e) => setDv({ ...dv, cm_version: e.target.value })}
                      className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                    >
                      <option value="v2.9">CM v2.9</option>
                      <option value="v4.0">CM v4.0</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">L6 MaxCLL (nits)</Label>
                    <Input
                      type="number" value={dv.l6_max_cll_nits}
                      onChange={(e) => setDv({ ...dv, l6_max_cll_nits: Number(e.target.value) })}
                      className="mt-1 bg-black border-zinc-800 font-mono text-xs"
                      data-testid="dv-maxcll"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">L6 MaxFALL (nits)</Label>
                    <Input
                      type="number" value={dv.l6_max_fall_nits}
                      onChange={(e) => setDv({ ...dv, l6_max_fall_nits: Number(e.target.value) })}
                      className="mt-1 bg-black border-zinc-800 font-mono text-xs"
                    />
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <input
                      id={`dv-l1-${channel.id}`}
                      type="checkbox"
                      checked={dv.l1_dynamic_metadata}
                      onChange={(e) => setDv({ ...dv, l1_dynamic_metadata: e.target.checked })}
                      className="accent-red-500"
                    />
                    <label htmlFor={`dv-l1-${channel.id}`} className="text-xs text-zinc-300">L1 dynamic per-shot metadata</label>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display font-bold">HLG · BT.2020 LUT</div>
                  <label className="flex items-center gap-1 text-[10px] text-zinc-400">
                    <input
                      type="checkbox"
                      checked={hlg.enabled}
                      onChange={(e) => setHlg({ ...hlg, enabled: e.target.checked })}
                      className="accent-red-500"
                      data-testid="hlg-enabled"
                    />
                    Enable
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">LUT</Label>
                    <select
                      value={hlg.lut_id}
                      onChange={(e) => {
                        const map = {
                          hlg_bt2020_default: "HLG BT.2020 Default",
                          hlg_bt2020_broadcast: "HLG BT.2020 Broadcast (BBC)",
                          hlg_bt2020_theatrical: "HLG BT.2020 Theatrical",
                          hlg_bt2020_sdr_compat: "HLG BT.2020 → SDR BT.709",
                        };
                        setHlg({ ...hlg, lut_id: e.target.value, lut_name: map[e.target.value] });
                      }}
                      className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                      data-testid="hlg-lut"
                    >
                      <option value="hlg_bt2020_default">HLG BT.2020 Default</option>
                      <option value="hlg_bt2020_broadcast">HLG BT.2020 Broadcast (BBC)</option>
                      <option value="hlg_bt2020_theatrical">HLG BT.2020 Theatrical</option>
                      <option value="hlg_bt2020_sdr_compat">HLG BT.2020 → SDR BT.709</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">System γ</Label>
                    <Input
                      type="number" step="0.05" value={hlg.hlg_system_gamma}
                      onChange={(e) => setHlg({ ...hlg, hlg_system_gamma: Number(e.target.value) })}
                      className="mt-1 bg-black border-zinc-800 font-mono text-xs"
                      data-testid="hlg-gamma"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Peak (nits)</Label>
                    <Input
                      type="number" value={hlg.peak_luminance_nits}
                      onChange={(e) => setHlg({ ...hlg, peak_luminance_nits: Number(e.target.value) })}
                      className="mt-1 bg-black border-zinc-800 font-mono text-xs"
                    />
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <input
                      id={`hlg-compat-${channel.id}`}
                      type="checkbox"
                      checked={hlg.sdr_compat_lut}
                      onChange={(e) => setHlg({ ...hlg, sdr_compat_lut: e.target.checked })}
                      className="accent-red-500"
                    />
                    <label htmlFor={`hlg-compat-${channel.id}`} className="text-xs text-zinc-300">Ship SDR BT.709 compat LUT alongside HLG</label>
                  </div>
                </div>
              </div>
            </div>
          )}
    </>
  );
}
